import { useCallback, useEffect, useRef } from 'react'
import { Signal } from '@preact/signals-react'
import { useBlockStateContext } from './BlockStateContext'
import { BlockStateFormatKey } from './ITaskBlock'

export function useBlockState<T>(
    key: string,
    initialValue?: T,
    formatKey: BlockStateFormatKey = BlockStateFormatKey.Default
): [Signal<T>, (value: T) => void] {
    const { getBlockState, setBlockState } = useBlockStateContext()

    const state = getBlockState(key) as Signal<T>

    if (initialValue !== undefined && state.value === null) {
        state.value = initialValue
    }

    state.key = formatKey

    const updateState = useCallback(
        (value: T) => {
            setBlockState(key, value)
        },
        [key, setBlockState]
    )

    return [state, updateState]
}

// Accept any function type that returns void
// T is unconstrained to allow any function signature, but we preserve the type T
// and use type assertions internally to convert to the storage format
export function useBlockListeners<T>(
    key: string,
    initialListener?: T
): [Signal<T | undefined>, (listener: T | undefined) => void] {
    const { getBlockListener, setBlockListener } = useBlockStateContext()

    const listener = getBlockListener(key) as Signal<T | undefined>

    if (initialListener !== undefined && listener.value === undefined) {
        listener.value = initialListener as unknown as (...args: unknown[]) => void as T
    }

    const updateListener = useCallback(
        (newListener: T | undefined) => {
            setBlockListener(
                key,
                newListener as unknown as ((...args: unknown[]) => void) | undefined
            )
        },
        [key, setBlockListener]
    )

    return [listener, updateListener]
}

export function useBlockMethod<T extends (...args: unknown[]) => unknown>(
    key: string,
    method: T
): void {
    const { setMethod } = useBlockStateContext()

    useEffect(() => {
        setMethod(key, method)
    }, [key, method, setMethod])
}

export function useBlockRef<T extends { getContainer: () => HTMLElement | null }>(ref: T) {
    const refObject = useRef<T>(ref)
    const refContainer = refObject.current?.getContainer()
    const { setRef } = useBlockStateContext()

    useEffect(() => {
        if (refContainer) {
            setRef(refObject)
        }
    }, [refContainer, setRef])
}
